import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import './SetupPassword.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [userName, setUserName] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirm: false
  });

  const validateToken = useCallback(async () => {
    if (!token || !email) {
      setMessage({ type: 'error', text: 'Invalid link. Please check your email for the correct invitation link.' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth-setup/validate-setup-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email })
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessages = {
          expired_token: 'This invitation link has expired. Please contact your administrator for a new invite.',
          invalid_token: 'This activation link has already been used or is invalid.',
          user_not_found: 'User not found. Please contact your administrator.'
        };
        setMessage({ type: 'error', text: errorMessages[data.error] || 'Invalid invitation link.' });
        setIsLoading(false);
        return;
      }

      setUserName(data.name || email.split('@')[0]);
      setIsLoading(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error validating invitation. Please try again later.' });
      setIsLoading(false);
    }
  }, [token, email]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) {
      errors.push(`Mínimo ${minLength} caracteres`);
    }
    if (!hasUpperCase) {
      errors.push('Al menos una letra mayúscula');
    }
    if (!hasLowerCase) {
      errors.push('Al menos una letra minúscula');
    }
    if (!hasNumbers) {
      errors.push('Al menos un número');
    }
    if (!hasSpecialChar) {
      errors.push('Al menos un carácter especial');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      if (!formData.password || !formData.confirmPassword) {
        throw new Error('Todos los campos son obligatorios');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('Las contraseñas no coinciden');
      }

      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        throw new Error(`La contraseña debe cumplir: ${passwordErrors.join(', ')}`);
      }

      const response = await fetch(`${API_BASE_URL}/auth-setup/complete-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password: formData.password })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'password_requirements_not_met') {
          throw new Error('La contraseña no cumple con los requisitos mínimos.');
        }
        if (data.error === 'expired_token') {
          throw new Error('Este enlace ha expirado. Por favor solicita uno nuevo.');
        }
        if (data.error === 'invalid_token') {
          throw new Error('Este enlace ya fue utilizado o es inválido.');
        }
        throw new Error(data.message || 'Error al configurar la contraseña');
      }

      setMessage({ type: 'success', text: '¡Contraseña configurada exitosamente! Redirigiendo...' });

      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    try {
      setMessage({ type: '', text: '' });
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/auth-setup/resend-setup-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al reenviar el correo');
      }

      setMessage({ type: 'info', text: 'Correo de invitación reenviado exitosamente.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthIndicator = () => {
    if (!formData.password) return null;

    const errors = validatePassword(formData.password);
    const strength = 5 - errors.length;
    const strengthLevels = ['Muy débil', 'Débil', 'Regular', 'Buena', 'Fuerte'];
    const strengthColors = ['#e53e3e', '#fd7f6f', '#fed7aa', '#9ae6b4', '#68d391'];

    return (
      <div className="password-strength">
        <div className="strength-bar">
          <div
            className="strength-fill"
            style={{
              width: `${(strength / 5) * 100}%`,
              backgroundColor: strengthColors[strength - 1] || '#e53e3e'
            }}
          />
        </div>
        <span className="strength-text" style={{ color: strengthColors[strength - 1] || '#e53e3e' }}>
          {strengthLevels[strength - 1] || 'Muy débil'}
        </span>
        {errors.length > 0 && (
          <ul className="strength-requirements">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="setup-password-container">
        <div className="setup-password-card">
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Validando invitación...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-password-container">
      <div className="setup-password-card">
        <div className="setup-password-header">
          <div className="logo">Dentaris</div>
          <h1>Configura tu contraseña</h1>
          <p>Hola {userName}, bienvenido a Dentaris. Crea tu contraseña para activar tu cuenta.</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {message.type !== 'success' && (
          <form onSubmit={handleSubmit} className="setup-password-form">
            <div className="form-group">
              <label htmlFor="password">Nueva Contraseña *</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.password ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  autoComplete="new-password"
                  placeholder="Ingresa tu nueva contraseña"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('password')}
                  aria-label={showPasswords.password ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={`fas ${showPasswords.password ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {getPasswordStrengthIndicator()}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Contraseña *</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  autoComplete="new-password"
                  placeholder="Confirma tu nueva contraseña"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('confirm')}
                  aria-label={showPasswords.confirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <div className="password-mismatch">
                  Las contraseñas no coinciden
                </div>
              )}
            </div>

            <div className="password-requirements">
              <h4>Requisitos de la contraseña:</h4>
              <ul>
                <li>Mínimo 8 caracteres</li>
                <li>Al menos una letra mayúscula</li>
                <li>Al menos una letra minúscula</li>
                <li>Al menos un número</li>
                <li>Al menos un carácter especial (!@#$%^&*)</li>
              </ul>
            </div>

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                icon="fas fa-key"
                disabled={!formData.password || !formData.confirmPassword}
              >
                {isSubmitting ? 'Configurando...' : 'Configurar contraseña'}
              </Button>
            </div>
          </form>
        )}

        <div className="resend-section">
          <p>¿No recibiste el correo?</p>
          <button
            type="button"
            className="resend-button"
            onClick={handleResendEmail}
            disabled={isSubmitting}
          >
            Reenviar correo de invitación
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupPassword;
